<template>
  <div
    class="font-primary font-medium text-[17px] text-gray-800 pb-6 border-b border-[#DFDFDF]"
  >
    General
  </div>

  <SettingsRow>
    <template #left><div class="my-auto">Profile photo</div></template>

    <template #right>
      <div class="flex items-center">
        <Avatar
          :src="user?.avatarUrl"
          :size="40"
          class="mr-4 cursor-pointer"
          @click="emit('openDialog', 'UploadProfilePicture')"
        />
        <div
          v-if="user"
          class="font-medium text-[14px] text-green-700 cursor-pointer"
          @click="emit('openDialog', 'UploadProfilePicture')"
        >
          Edit
        </div>
        <Skeleton v-else class="w-[30px] h-[18px]" />
      </div>
    </template>
  </SettingsRow>

  <div
    class="w-full h-auto! min-h-[70px] py-[26px] font-primary text-[14px] leading-[18px] text-gray-800 border-b border-[#DFDFDF]"
  >
    <div class="w-full flex justify-between">
      <div class="whitespace-nowrap mr-4">Name</div>

      <div class="flex">
        <template v-if="user">
          <div class="mr-4 text-right [word-break:break-word] max-w-[720px]">
            {{ user?.firstName }} {{ user?.lastName }}
          </div>
          <div
            class="font-medium text-[14px] text-green-700 cursor-pointer"
            @click="emit('openDialog', 'ChangeName')"
          >
            Edit
          </div>
        </template>
        <template v-else>
          <Skeleton class="w-[100px] h-[18px] mr-4" />
          <Skeleton class="w-[30px] h-[18px]" />
        </template>
      </div>
    </div>

    <template v-if="originFlagEnabled">
      <div
        v-if="identityUnverified"
        class="w-full px-[15px] py-2.5 bg-green-200/30 rounded-[10px] flex items-center justify-between mt-6"
      >
        <div>
          <div
            class="font-secondary text-[14px] leading-[20px] font-medium text-gray-800"
          >
            Verify your account
          </div>
          <div
            class="font-secondary text-[13px] leading-[19px] text-gray-800/75"
          >
            Confirm your identity with a government issued ID to provide a
            higher level of validation for your copyrights
          </div>
        </div>

        <Icon
          v-if="identityLoading"
          name="Loading"
          :size="20"
          class="stroke-green-700 animate-spin"
        />

        <div
          v-else
          class="font-secondary text-link text-[14px] cursor-pointer"
          @click="emit('verifyIdentity')"
        >
          Verify
        </div>
      </div>

      <div
        v-if="identityProcessing"
        class="w-full px-[15px] py-2.5 bg-gray-200/30 rounded-[10px] flex items-center mt-6"
      >
        <img :src="SpinnerGray" class="size-[18px] mr-[14px] animate-spin" />
        <div class="font-medium">We're reviewing your details</div>
      </div>
    </template>
  </div>

  <SettingsRow class="h-auto! min-h-[70px] py-[26px]">
    <template #left>
      <div class="whitespace-nowrap mr-4">Copyright owners</div>
    </template>

    <template #right>
      <div class="flex">
        <template v-if="copyrightOwners?.length">
          <div class="mr-4 text-right [word-break:break-word] max-w-[720px]">
            <div
              v-for="owner in copyrightOwners?.slice(0, 5)"
              :key="owner.id"
              class="flex items-center font-primary text-[14px] leading-[18px] mb-3 last:mb-0"
            >
              <Avatar :src="owner.avatarUrl" :size="22" class="mr-2" />
              <div class="text-left line-clamp-3">{{ owner.name }}</div>
            </div>
            <div
              v-if="copyrightOwnersLeft"
              class="font-primary text-[14px] opacity-50 text-gray-800 text-left"
            >
              + {{ copyrightOwnersLeft }}
            </div>
          </div>
          <div
            class="h-fit font-medium text-[14px] text-green-700 cursor-pointer"
            @click="emit('openDialog', 'CopyrightOwners')"
          >
            {{ copyrightOwners.length ? 'Edit' : 'Add' }}
          </div>
        </template>
        <template v-else>
          <Skeleton class="w-[80px] h-[18px] mr-4" />
          <Skeleton class="w-[30px] h-[18px]" />
        </template>
      </div>
    </template>
  </SettingsRow>
</template>

<script setup lang="ts">
import SettingsRow from '@/features/settings/components/SettingsRow.vue'
import { Icon, Avatar, Skeleton } from '@dacr/ui'
import SpinnerGray from '@/assets/images/SpinnerGray.png'
import type {
  CopyrightOwner,
  UserFieldsFragment,
} from '@/shared/graphql/legacy/generated'

const COPYRIGHT_OWNERS_THRESHOLD = 5

defineProps<{
  user: UserFieldsFragment | null
  copyrightOwners?: CopyrightOwner[]
  copyrightOwnersLeft?: string
  originFlagEnabled: boolean
  identityUnverified: boolean
  identityProcessing: boolean
  identityLoading: boolean
}>()

const emit = defineEmits<{
  (e: 'openDialog', name: string): void
  (e: 'verifyIdentity'): void
}>()
</script>
